(() => {
  const productCardHref = handle => {
    if (!handle) return '';
    const card = [...document.querySelectorAll('[data-product-handle]')]
      .find(node => node.dataset.productHandle === handle);
    return card?.querySelector('a.product-card__link')?.getAttribute('href') || '';
  };

  const hardenHouseFixLink = link => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const handle = link.dataset.houseProductHandle || '';
    const liquidUrl = link.dataset.houseProductUrl || '';
    const workingCardUrl = productCardHref(handle);
    const href = workingCardUrl || liquidUrl || link.getAttribute('href') || '';
    if (!href) return;

    link.setAttribute('href', href);
    const fix = link.closest('.gooey-house__fix');
    const mediaLink = fix?.querySelector('a.gooey-house__fix-media');
    if (mediaLink) mediaLink.setAttribute('href', href);
  };

  const hardenProductLinks = root => {
    root.querySelectorAll?.('[data-house-fix-link]').forEach(hardenHouseFixLink);
  };

  const guard404 = () => {
    const root = document.querySelector('[data-gooey-404-root]');
    if (!root) return;

    const moments = [...root.querySelectorAll('.gooey-layer3-404__moment')];
    const firstMoment = moments[0];
    if (firstMoment) {
      firstMoment.setAttribute('data-gooey-layer3-404', 'true');
      moments.slice(1).forEach(node => node.remove());
    }

    [...root.querySelectorAll('.gooey-layer3-404__figure')].slice(1).forEach(node => node.remove());
    [...root.querySelectorAll('.gooey-layer3-404__speech')].slice(1).forEach(node => node.remove());
  };

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const routeRoot = () => String(window.GooeyDuck?.routes?.root || '/').replace(/#.*$/, '');
  const routeFromRoot = path => `${routeRoot().replace(/\/?$/, '/')}${String(path || '').replace(/^\/+/, '')}`;
  const houseSectionUrl = () => `${routeRoot()}#gooey-duck-house`;
  const scentFinderUrl = () => window.GooeyDuck?.routes?.scentFinder || `${routeFromRoot('search')}?view=scent-finder`;
  const candlesUrl = () => routeFromRoot('collections/candles');
  const rewardsUrl = () => routeFromRoot('pages/rewards');

  const experienceState = () => window.GooeyExperience?.state?.get?.() || null;
  const hasHouseContext = snapshot => Boolean(
    snapshot?.currentRoom ||
    snapshot?.currentSituation ||
    snapshot?.currentProblem ||
    snapshot?.continuation?.room ||
    snapshot?.continuation?.situation
  );

  const conciergeEnabled = () => (
    window.GooeyDuck?.experience?.enabled !== false &&
    window.GooeyDuck?.experience?.gooeyCommentary !== false &&
    !document.body?.classList.contains('experience-gooey-off')
  );

  const createFigure = pose => {
    const figure = create('figure', 'gooey-layer3-figure gooey-layer3-dock-hero__figure');
    figure.dataset.gooeyPose = pose;
    figure.setAttribute('aria-hidden', 'true');
    const image = document.createElement('img');
    image.src = window.GooeyDuck?.mascotAssets?.[pose] || window.GooeyDuck?.mascotAssets?.neutral || '';
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    figure.appendChild(image);
    return figure;
  };

  const createSpeech = line => {
    const bubble = create('div', 'gooey-layer3-speech gooey-layer3-dock-hero__speech');
    bubble.append(
      create('span', 'gooey-layer3-speech__label', 'GOOEY'),
      create('p', 'gooey-layer3-speech__copy', line)
    );
    const scribble = create('span', 'gooey-layer3-speech__scribble');
    scribble.setAttribute('aria-hidden', 'true');
    bubble.append(scribble);
    return bubble;
  };

  const buildConciergeDock = () => {
    const dock = create('div', 'gooey-rescue-dock gooey-concierge-dock');
    dock.dataset.gooeyConciergeDock = 'true';
    dock.dataset.gooeyDockMode = 'concierge';

    const trigger = create('button', 'gooey-rescue-dock__trigger');
    trigger.type = 'button';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'GooeyConciergePanel');
    trigger.setAttribute('aria-label', 'Open Gooey help');
    trigger.append(
      create('span', 'gooey-rescue-dock__trigger-main', 'GOOEY'),
      create('span', 'gooey-rescue-dock__trigger-room', 'ASK ME')
    );

    const panel = create('aside', 'gooey-rescue-dock__panel gooey-layer3-dock gooey-layer3-dock--concierge');
    panel.id = 'GooeyConciergePanel';
    panel.hidden = true;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-label', 'Gooey shopping help');

    const head = create('div', 'gooey-rescue-dock__head');
    const headCopy = create('div');
    headCopy.append(create('p', 'eyebrow', 'Gooey'), create('h2', 'h3', 'Pick your problem.'));
    const close = create('button', 'gooey-rescue-dock__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close Gooey help');
    head.append(headCopy, close);

    const hero = create('div', 'gooey-layer3-dock-hero');
    hero.dataset.gooeyLayer3Dock = 'true';
    hero.append(createFigure('sideEye'), createSpeech('Need a hand? I have opinions.'));

    const actions = create('div', 'gooey-rescue-dock__actions');
    const scent = create('a', 'button button--accent', 'Find My Scent →');
    scent.href = scentFinderUrl();
    const room = create('a', 'button button--outline', 'Fix a Room →');
    room.href = houseSectionUrl();
    const rewards = create('a', 'button button--outline', 'Join / View Rewards →');
    rewards.href = rewardsUrl();
    const shop = create('a', 'button button--outline', 'Just Show Me the Candles →');
    shop.href = candlesUrl();
    actions.append(scent, room, rewards, shop);

    panel.append(head, hero, actions);
    dock.append(trigger, panel);

    const setOpen = open => {
      trigger.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      dock.classList.toggle('is-open', open);
      if (open) close.focus({ preventScroll: true });
    };

    trigger.addEventListener('click', () => setOpen(trigger.getAttribute('aria-expanded') !== 'true'));
    close.addEventListener('click', () => {
      setOpen(false);
      trigger.focus({ preventScroll: true });
    });
    panel.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.focus({ preventScroll: true });
    });

    return dock;
  };

  const syncConciergeDock = () => {
    const existing = document.querySelector('[data-gooey-concierge-dock]');
    const houseDock = document.querySelector('[data-gooey-rescue-dock]');
    const snapshot = experienceState();
    const on404 = Boolean(document.querySelector('[data-gooey-404-root]'));
    const shouldShow = conciergeEnabled() && !hasHouseContext(snapshot) && !on404;

    if (!shouldShow || houseDock) {
      existing?.remove();
      return;
    }

    if (!existing && document.body) document.body.append(buildConciergeDock());
  };

  let queued = false;
  const reconcile = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      hardenProductLinks(document);
      guard404();
      syncConciergeDock();
    });
  };

  document.addEventListener('click', event => {
    const link = event.target instanceof Element ? event.target.closest('[data-house-fix-link]') : null;
    if (link) hardenHouseFixLink(link);
  }, true);
  document.addEventListener('gooey:experience:statechange', reconcile);
  document.addEventListener('shopify:section:load', reconcile);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  else reconcile();
})();